import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ModalShell from '../../shared/components/modal/ModalShell';
import ModalFormFooter from '../../shared/components/modal/ModalFormFooter';
import FormField from '../../shared/components/modal/FormField';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import useFormState from '../../shared/hooks/useFormState';
import useOrganisationStore from '../../store/organisations-store';
import { trimData } from '../../shared/lib/formatters';

const EditUserModal = ({ isOpen, onRequestClose, user, onEdit }) => {
  const searchOrganisations = useOrganisationStore((s) => s.searchOrganisations);

  const initialValues = React.useMemo(
    () => ({
      name: user?.name || '',
      organisation_id: user?.organisation_id || '',
      is_admin: user?.is_admin || false,
    }),
    [user]
  );

  const { formData, handleChange, setField, isLoading, setIsLoading, isSubmitDisabled } = useFormState({
    initialValues,
    isOpen,
    mode: 'edit',
  });

  const [orgLabel, setOrgLabel] = useState('');

  useEffect(() => {
    if (isOpen) {
      setOrgLabel(user?.organisation_name || '');
    }
  }, [isOpen, user]);

  const loadOrgOptions = useCallback(
    (search) => searchOrganisations({ search }),
    [searchOrganisations]
  );

  const handleCancel = () => onRequestClose();

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onEdit(user.id, trimData(formData));
      toast.success('User updated successfully');
      onRequestClose();
    } catch (error) {
      console.error('Failed to edit user:', error);
      toast.error(error.message || 'Failed to edit user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const footer = (
    <ModalFormFooter
      onCancel={handleCancel}
      onSubmit={handleSave}
      isLoading={isLoading}
      isDisabled={isSubmitDisabled}
    />
  );

  return (
    <ModalShell isOpen={isOpen} onClose={handleCancel} title="Edit User" footer={footer} maxWidthClass="max-w-xl">
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            field={{ name: 'name', label: 'Name', type: 'text', required: true }}
            value={formData.name}
            onChange={handleChange}
          />
          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-bold text-gray-700">Organisation</label>
            <SearchableSelect
              name="organisation_id"
              value={formData.organisation_id}
              selectedLabel={orgLabel}
              onChange={(id, option) => {
                setField('organisation_id', id || '');
                setOrgLabel(option?.name || '');
              }}
              loadOptions={loadOrgOptions}
              placeholder="Select Organisation"
              className="w-full"
              required
            />
          </div>
          <FormField
            field={{ name: 'is_admin', label: 'Is Admin', type: 'checkbox' }}
            value={formData.is_admin}
            onChange={handleChange}
          />
        </div>
      </form>
    </ModalShell>
  );
};

export default EditUserModal;
